// src/components/ProductDetail.js
// src/components/ProductDetail.js
import React, { useEffect, useRef } from 'react';
import { ArrowRight, MessageCircle, Plus } from 'lucide-react';

export default function ProductDetail({
  product: selectedProduct,
  onClose,
  colors,
  paymentMethod,
  calculatePrice,
  getAvailableInstallments,
  sendWhatsApp,
  addToCart,
  creditOptions,
  accentColor,
  shippingConfig,
  activePromo
}) {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Push state to history to enable back button closing
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      // When back button is pressed, call the onClose prop
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleManualClose = () => {
    // Manually going back triggers popstate, which calls onClose
    window.history.back();
  };

  if (!selectedProduct) return null;

  const hasValue = (v) =>
    v !== null && v !== undefined && String(v).trim() !== '' && String(v) !== '-' && v !== 0 && String(v) !== '0';

  const getCardDetails = () => {
    if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId) {
      return creditOptions.find((opt) => opt.id === paymentMethod.cardId);
    }
    return null;
  };

  const cardDetails = getCardDetails();

  const reintegroAmount =
    cardDetails && cardDetails.discount > 0
      ? Math.round((calculatePrice(selectedProduct) * cardDetails.discount) / 100)
      : 0;

  const getShippingCost = () => {
    if (!shippingConfig) return 0;
    const points = Number(selectedProduct.puntosRegulares) || 0;
    if (points >= shippingConfig.minimumPoints) return 0;

    const isCash = paymentMethod.type === 'efectivo' || paymentMethod.type === 'cuotas_efectivo';
    return isCash ? shippingConfig.cashPrice : shippingConfig.cardPrice;
  };

  const shippingCost = getShippingCost();

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={handleManualClose}
    >
      <div
        className="bg-white max-w-2xl w-full my-8 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* --- HEADER (Imagen) --- */}
        <div
          className="relative top-0 z-20 px-8 py-6 rounded-t-3xl bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${selectedProduct.imagen})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="flex items-center justify-end">
            <button
              onClick={handleManualClose}
              className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-800 px-4 py-2 rounded-full flex items-center gap-2 transition shadow-lg font-semibold z-10"
            >
              <ArrowRight className="w-5 h-5" />
              Volver
            </button>
          </div>
          <h2 className="text-3xl font-bold text-white mt-4">
            {selectedProduct.nombre}
          </h2>
        </div>

        {/* --- CONTENEDOR PRINCIPAL CON PADDING (Lo que faltaba) --- */}
        <div className="p-6 md:p-8">

          {/* Bloque de Precios */}
          <div
            className={`p-6 rounded-2xl mb-8 flex flex-col gap-4 border-l-4 ${!accentColor ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-600' : 'bg-gray-50'}`}
            style={accentColor ? { borderColor: accentColor } : {}}
          >
            {paymentMethod.type === 'efectivo' || paymentMethod.type === 'cuotas_efectivo' ? (
              <>
                <p className={`text-sm font-semibold ${!accentColor ? 'text-purple-600' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                  {paymentMethod.type === 'cuotas_efectivo' ? 'Precio en Cuotas Efectivo' : 'Precio en Efectivo'}
                </p>
                <p className={`text-3xl font-bold ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                  ${calculatePrice(selectedProduct).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                </p>
                {activePromo && activePromo.discountPercentage && (
                  <span className="inline-block mt-1 bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs font-bold w-fit">
                    ¡{activePromo.discountPercentage}% OFF Activado!
                  </span>
                )}
                {(() => {
                  if (selectedProduct.discount > 0 && selectedProduct.discountEndDate) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const endDate = new Date(selectedProduct.discountEndDate + 'T00:00:00');
                    endDate.setHours(0, 0, 0, 0);
                    if (endDate >= today) {
                      return (
                        <span className="inline-block mt-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-bold w-fit">
                          ¡{selectedProduct.discount}% OFF Especial!
                        </span>
                      );
                    }
                  }
                  return null;
                })()}
              </>
            ) : (
              <div>
                <p className={`text-sm font-semibold ${!accentColor ? 'text-purple-600' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                  {cardDetails
                    ? `Precio con ${cardDetails.type} - ${cardDetails.bank}`
                    : 'Precio de Lista (Tarjeta)'}
                </p>
                {calculatePrice(selectedProduct).toLocaleString('es-AR', { maximumFractionDigits: 0 }) !== (selectedProduct.list_price || selectedProduct.precio).toLocaleString('es-AR', { maximumFractionDigits: 0 }) && (
                  <div className="flex items-center gap-2">
                    <p className={`text-3xl font-bold ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                      ${calculatePrice(selectedProduct).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                    </p>
                    {selectedProduct.discount > 0 && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold">
                        -{selectedProduct.discount}%
                      </span>
                    )}
                  </div>
                )}
                {calculatePrice(selectedProduct).toLocaleString('es-AR') === (selectedProduct.list_price || selectedProduct.precio).toLocaleString('es-AR') && (
                  <p className={`text-3xl font-bold ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                    ${calculatePrice(selectedProduct).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                  </p>
                )}
                {cardDetails && cardDetails.discount > 0 && (
                  <span className="inline-block mt-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    Reintegro de ${reintegroAmount.toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
            )}

            {/* Shipping and Total - grouped to reduce space */}
            <div className={`pt-2 border-t border-dashed ${!accentColor ? 'border-purple-200' : 'border-gray-200'} space-y-1`}>
              {shippingConfig && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Envío</span>
                  <span className={`font-bold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                    {shippingCost === 0 ? '¡Gratis!' : `$${shippingCost.toLocaleString('es-AR')}`}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-bold">Total Final</span>
                <span className="font-extrabold text-gray-900">
                  ${(calculatePrice(selectedProduct) + shippingCost).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            {/* Installments List - Moved after shipping/total */}
            {paymentMethod.type !== 'efectivo' && paymentMethod.type !== 'cuotas_efectivo' && (
              <div className="space-y-3 mt-4">
                {getAvailableInstallments().map((installment) => {
                  const totalWithShipping = calculatePrice(selectedProduct) + shippingCost;
                  const installmentAmount = totalWithShipping / installment;
                  return (
                    <div
                      key={installment}
                      className="flex justify-between items-center bg-white p-3 rounded-xl shadow-sm"
                    >
                      <span className="font-semibold text-gray-700 text-sm md:text-base">
                        {installment === 1 ? 'Pago único' : `${installment} cuotas s/int.`}
                      </span>
                      <span className={`text-lg md:text-xl font-bold ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                        ${installmentAmount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Descripción */}
          <div className="mb-8">
            <h3 className="text-gray-800 font-semibold mb-3 text-lg">Descripción</h3>
            <p className="text-gray-600 leading-relaxed">{selectedProduct.descripcion}</p>
          </div>

          {/* Especificaciones Técnicas */}
          <div className="mb-8">
            <h3 className="text-gray-800 font-semibold mb-3 text-lg">Especificaciones Técnicas</h3>
            <div
              className={`rounded-2xl overflow-hidden border-2 ${!accentColor ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100' : 'bg-gray-50'}`}
              style={accentColor ? { borderColor: `${accentColor}40` } : {}}
            >
              <table className="w-full text-sm md:text-base">
                <tbody>
                  {hasValue(selectedProduct.linea) && (
                    <tr className={`border-b ${!accentColor ? 'border-purple-200' : 'border-gray-200'}`}>
                      <td className={`px-4 py-3 font-semibold bg-white/50 w-1/3 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Línea</td>
                      <td className="px-4 py-3 text-gray-800">{selectedProduct.linea}</td>
                    </tr>
                  )}
                  {hasValue(selectedProduct.diametro) && (
                    <tr className={`border-b ${!accentColor ? 'border-purple-200' : 'border-gray-200'}`}>
                      <td className={`px-4 py-3 font-semibold bg-white/50 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Diámetro</td>
                      <td className="px-4 py-3 text-gray-800">{`${selectedProduct.diametro} cm`}</td>
                    </tr>
                  )}
                  {hasValue(selectedProduct.capacidad) && (
                    <tr className={`border-b ${!accentColor ? 'border-purple-200' : 'border-gray-200'}`}>
                      <td className={`px-4 py-3 font-semibold bg-white/50 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Capacidad</td>
                      <td className="px-4 py-3 text-gray-800">{`${selectedProduct.capacidad} litros`}</td>
                    </tr>
                  )}
                  {hasValue(selectedProduct.comensales) && (
                    <tr className={`border-b ${!accentColor ? 'border-purple-200' : 'border-gray-200'}`}>
                      <td className={`px-4 py-3 font-semibold bg-white/50 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Comensales</td>
                      <td className="px-4 py-3 text-gray-800">{`${selectedProduct.comensales} personas`}</td>
                    </tr>
                  )}
                  {(() => {
                    const vals = Array.isArray(selectedProduct.color)
                      ? selectedProduct.color
                      : selectedProduct.color
                        ? [selectedProduct.color]
                        : [];
                    if (vals.length === 0) return null;
                    return (
                      <tr className={`border-b ${!accentColor ? 'border-purple-200' : 'border-gray-200'}`}>
                        <td className={`px-4 py-3 font-semibold bg-white/50 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Color</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {vals.map((c) => {
                                const meta = colors[c] || {};
                                return (
                                  <span
                                    key={c}
                                    className="w-5 h-5 rounded-full border border-purple-300 shadow-sm"
                                    style={{ backgroundColor: meta.hex || '#e5e7eb' }}
                                    title={meta.name || c}
                                  />
                                );
                              })}
                            </div>
                            <div className="text-gray-800 text-sm">
                              {vals
                                .map((c) => (colors[c] && colors[c].name ? colors[c].name : c))
                                .join(', ')}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                  {hasValue(selectedProduct.revestimiento) && (
                    <tr>
                      <td className={`px-4 py-3 font-semibold bg-white/50 ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>Revestimiento</td>
                      <td className="px-4 py-3 text-gray-800">
                        {Array.isArray(selectedProduct.revestimiento)
                          ? selectedProduct.revestimiento.join(', ')
                          : selectedProduct.revestimiento}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex flex-col sm:flex-row gap-3 mt-auto">
            <button
              onClick={() => {
                addToCart(selectedProduct);
                handleManualClose();
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold transition shadow-md active:scale-95 ${!accentColor ? 'bg-blue-600 hover:bg-blue-700' : 'hover:opacity-90'}`}
              style={accentColor ? { backgroundColor: accentColor } : {}}
            >
              <Plus className="w-5 h-5" />
              Agregar al combo
            </button>

            <button
              onClick={() => {
                sendWhatsApp(selectedProduct);
                handleManualClose();
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold transition shadow-md active:scale-95 ${!accentColor ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' : 'hover:opacity-90'}`}
              style={accentColor ? { backgroundColor: accentColor } : {}}
            >
              <MessageCircle className="w-5 h-5" />
              Consultar
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}